import { App, AppConfig, AppLoaderConstructor, TightAppLoaderConfig } from '@nab/x-types';
import { XV1AppLoader } from './XV1AppLoader';
import { XV2AppLoader } from './XV2AppLoader';

export type LoaderMatcher = string | string[] | LoaderMatchFn;

export interface LoaderMatchFn {
    (appMetaData: App, appConfig?: AppConfig): boolean;
}

export interface LoaderStrategy<APP extends App = App> {
    constructor: AppLoaderConstructor<APP>;
    supports: LoaderMatcher;
}

const customLoaders: LoaderStrategy[] = [];
const defaultLoaders: LoaderStrategy[] = [
    {
        constructor: XV2AppLoader,
        supports: (appMetaData: App) => appMetaData.appType === 'XV2' || appMetaData.appType === 'XV2SPA'
    },
    { constructor: XV1AppLoader, supports: (appMetaData: App) => appMetaData.appType === 'XV1' }
];

export function registerLoader(strategy: LoaderStrategy, isLowPriority: boolean = false) {
    let matcher: LoaderMatchFn;

    if (typeof strategy.supports === 'string' || Array.isArray(strategy.supports)) {
        const appType = typeof strategy.supports === 'string' ? [strategy.supports] : strategy.supports;
        matcher = (appMetaData: App, appConfig: AppConfig) => appType.indexOf(appMetaData.appType) !== -1;
    } else {
        matcher = strategy.supports;
    }

    const augmentedStrategy = {
        constructor: strategy.constructor,
        supports: matcher
    };

    if (isLowPriority) {
        customLoaders.push(augmentedStrategy);
    } else {
        customLoaders.unshift(augmentedStrategy);
    }
}

export function getLoaderConstructor<APP extends App = App>(appMetaData: App, appConfig?: AppConfig): AppLoaderConstructor<APP> {
    const loaderStrategy = <LoaderStrategy<APP>>(
        [...customLoaders, ...defaultLoaders].find((l) => (<LoaderMatchFn>l.supports)(appMetaData, appConfig))
    );
    if (!loaderStrategy) {
        throw new Error(`Failed to find a suitable app loader for app ${appMetaData.id} with appType ${appMetaData.appType}`);
    }
    return loaderStrategy.constructor;
}

/**
 * Get base class of a target class
 */
export function getBaseClass(targetClass): any {
    if (targetClass instanceof Function) {
        let baseClass = targetClass;

        while (baseClass) {
            const newBaseClass = Object.getPrototypeOf(baseClass);

            if (newBaseClass && newBaseClass !== Object && newBaseClass.name) {
                baseClass = newBaseClass;
            } else {
                break;
            }
        }

        return baseClass;
    }
    return false;
}

/**
 * updateAllLoaderInstances for unique loader constructors that support the same.
 */
export function updateAllLoaderInstances(config: TightAppLoaderConfig, updateUserOnly?: boolean) {
    const uniqueConstructors = new Set<AppLoaderConstructor>();
    [...customLoaders, ...defaultLoaders].forEach((s) => {
        const fn = (s.constructor as any).updateAllLoaderInstances;
        if (fn) {
            const baseClass = getBaseClass(s.constructor);
            if (baseClass && (baseClass as any).updateAllLoaderInstances) {
                uniqueConstructors.add(baseClass);
            } else {
                uniqueConstructors.add(s.constructor);
            }
        }
    });
    uniqueConstructors.forEach((loaderConstructor) => {
        (loaderConstructor as any).updateAllLoaderInstances(config, updateUserOnly);
    });
}

export function reset() {
    customLoaders.splice(0, customLoaders.length);
}
